import { useRef } from "react"
import { Loader2, Upload } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button, type buttonVariants } from "@/components/ui/button"
import type { VariantProps } from "class-variance-authority"

interface UploadButtonProps {
  onPick: (file: File) => void
  disabled?: boolean
  isUploading?: boolean
  size?: VariantProps<typeof buttonVariants>["size"]
  variant?: VariantProps<typeof buttonVariants>["variant"]
  accept?: string
  label?: string
  multiple?: boolean
  onPickMany?: (files: File[]) => void
}

// Wraps a hidden <input type="file"> behind a real button. Most callers
// only need single-file behaviour; pass `multiple` + `onPickMany` for
// the photos card.
export function UploadButton({
  onPick,
  disabled,
  isUploading,
  size = "sm",
  variant = "outline",
  accept,
  label,
  multiple,
  onPickMany,
}: UploadButtonProps) {
  const { t } = useTranslation("transit")
  const inputRef = useRef<HTMLInputElement | null>(null)

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          if (files.length === 0) return
          if (multiple && onPickMany) onPickMany(files)
          else onPick(files[0])
          if (inputRef.current) inputRef.current.value = ""
        }}
      />
      <Button
        type="button"
        variant={variant}
        size={size}
        disabled={disabled || isUploading}
        onClick={() => inputRef.current?.click()}
      >
        {isUploading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Upload className="size-4" />
        )}
        {label ?? t("docCard.upload")}
      </Button>
    </>
  )
}
